import { useState, useEffect, useCallback } from "react";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../../firebase/config";
import { NHS_MILESTONES, getBracketsForAge, getCurrentBracket } from "../../utils/nhsMilestones";

const DOMAIN_COLORS = {
  social:    { bg: "#FFF9C4", border: "#F9A825", text: "#E65100" },
  language:  { bg: "#E3F2FD", border: "#1E88E5", text: "#0D47A1" },
  cognitive: { bg: "#F3E5F5", border: "#8E24AA", text: "#4A148C" },
  motor:     { bg: "#E8F5E9", border: "#43A047", text: "#1B5E20" },
};

const ProgressRing = ({ checked, total }) => {
  const pct = total === 0 ? 0 : checked / total;
  const color = pct === 1 ? "#43A047" : pct > 0.5 ? "#FFA726" : "#EF5350";
  return (
    <span style={{ fontSize: "0.78rem", fontWeight: 700, color }}>
      {checked}/{total}
    </span>
  );
};

const MilestoneChecklist = ({ childId, childDOB }) => {
  const [checked, setChecked]         = useState({});
  const [activeBracket, setActiveBracket] = useState(null);
  const [saving, setSaving]           = useState(false);

  // Calculate age in months
  const ageMonths = childDOB
    ? Math.floor((Date.now() - new Date(childDOB).getTime()) / (1000 * 60 * 60 * 24 * 30.4375))
    : null;

  const visibleBrackets = getBracketsForAge(ageMonths);
  const currentBracket  = getCurrentBracket(ageMonths);

  // Default to current bracket on load
  useEffect(() => {
    if (currentBracket && activeBracket === null) {
      setActiveBracket(currentBracket);
    }
  }, [currentBracket, activeBracket]);

  // Load checked state from Firestore
  useEffect(() => {
    if (!childId) return;
    getDoc(doc(db, "milestoneChecklist", childId)).then((snap) => {
      if (snap.exists()) setChecked(snap.data().checked || {});
    });
  }, [childId]);

  const toggleItem = useCallback(async (key) => {
    const next = { ...checked, [key]: !checked[key] };
    setChecked(next);
    if (!childId) return;
    setSaving(true);
    try {
      await setDoc(doc(db, "milestoneChecklist", childId), { checked: next });
    } finally {
      setSaving(false);
    }
  }, [checked, childId]);

  if (!childDOB) {
    return (
      <div className="card">
        <div className="card-body">
          <div className="empty-state">
            <div className="empty-state-icon">🎯</div>
            <p>Add date of birth to the child profile to see age-appropriate milestones.</p>
          </div>
        </div>
      </div>
    );
  }

  if (visibleBrackets.length === 0) {
    return (
      <div className="card">
        <div className="card-body">
          <div className="empty-state">
            <div className="empty-state-icon">👶</div>
            <p>Milestones will appear here starting from 2 months.</p>
          </div>
        </div>
      </div>
    );
  }

  const bracketData = activeBracket ? NHS_MILESTONES[activeBracket] : null;

  // Count totals per bracket for the tab badges
  const countForBracket = (b) => {
    const data = NHS_MILESTONES[b];
    if (!data) return { total: 0, done: 0 };
    let total = 0, done = 0;
    data.domains.forEach((domain) => {
      domain.items.forEach((_, idx) => {
        total++;
        if (checked[`${b}_${domain.key}_${idx}`]) done++;
      });
    });
    return { total, done };
  };

  return (
    <div className="card">
      <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
        <span style={{ fontWeight: 600, fontSize: "0.95rem" }}>
          NHS Developmental Milestones
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          {saving && (
            <span style={{ fontSize: "0.75rem", color: "var(--text-light)" }}>Saving…</span>
          )}
          {ageMonths != null && (
            <span style={{
              fontSize: "0.78rem", fontWeight: 600,
              background: "rgba(var(--primary-soft-rgb,255,179,217),0.2)",
              color: "var(--primary-dark)",
              padding: "0.2rem 0.6rem", borderRadius: "50px",
              border: "1px solid var(--primary-soft)",
            }}>
              Age: {ageMonths}m
            </span>
          )}
        </div>
      </div>

      <div className="card-body">
        {/* Age bracket tabs */}
        <div style={{
          display: "flex", gap: "0.4rem", flexWrap: "wrap",
          marginBottom: "1.25rem", borderBottom: "1px solid var(--border-light)",
          paddingBottom: "0.75rem",
        }}>
          {visibleBrackets.map((b) => {
            const { total, done } = countForBracket(b);
            const isActive = activeBracket === b;
            const isCurrent = b === currentBracket;
            const isComplete = done === total && total > 0;
            return (
              <button
                key={b}
                onClick={() => setActiveBracket(b)}
                style={{
                  padding: "0.3rem 0.75rem",
                  borderRadius: "50px",
                  border: isActive
                    ? "2px solid var(--primary-color,#FF85C0)"
                    : "1px solid var(--border-light)",
                  background: isActive
                    ? "rgba(var(--primary-soft-rgb,255,179,217),0.15)"
                    : "transparent",
                  cursor: "pointer",
                  fontSize: "0.82rem",
                  fontWeight: isActive ? 700 : 500,
                  color: isActive ? "var(--primary-dark)" : "var(--text-dark)",
                  display: "flex", alignItems: "center", gap: "0.35rem",
                }}
              >
                {NHS_MILESTONES[b]?.label}
                {isCurrent && !isComplete && (
                  <span style={{
                    width: "6px", height: "6px", borderRadius: "50%",
                    background: "var(--primary-color,#FF85C0)", display: "inline-block",
                  }} />
                )}
                {isComplete && <span style={{ color: "#43A047" }}>✓</span>}
                {!isComplete && total > 0 && (
                  <ProgressRing checked={done} total={total} />
                )}
              </button>
            );
          })}
        </div>

        {/* Domain checklists */}
        {bracketData && (
          <div className="milestone-domain-grid">
            {bracketData.domains.map((domain) => {
              const colors = DOMAIN_COLORS[domain.key] || DOMAIN_COLORS.cognitive;
              const domainDone  = domain.items.filter((_, i) => checked[`${activeBracket}_${domain.key}_${i}`]).length;
              const domainTotal = domain.items.length;
              return (
                <div
                  key={domain.key}
                  style={{
                    border: `1px solid ${colors.border}`,
                    borderRadius: "10px",
                    overflow: "hidden",
                  }}
                >
                  {/* Domain header */}
                  <div style={{
                    background: colors.bg,
                    padding: "0.6rem 0.9rem",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}>
                    <span style={{ fontWeight: 700, fontSize: "0.85rem", color: colors.text }}>
                      {domain.icon} {domain.label}
                    </span>
                    <span style={{ fontSize: "0.78rem", fontWeight: 600, color: colors.text }}>
                      {domainDone}/{domainTotal}
                    </span>
                  </div>

                  {/* Milestone items */}
                  <div style={{ padding: "0.6rem 0.9rem" }}>
                    {domain.items.map((item, idx) => {
                      const key = `${activeBracket}_${domain.key}_${idx}`;
                      const isDone = !!checked[key];
                      return (
                        <label
                          key={key}
                          style={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: "0.6rem",
                            cursor: "pointer",
                            padding: "0.35rem 0",
                            borderBottom: idx < domain.items.length - 1
                              ? "1px solid var(--border-light)" : "none",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={isDone}
                            onChange={() => toggleItem(key)}
                            style={{ marginTop: "0.15rem", accentColor: colors.border, flexShrink: 0 }}
                          />
                          <span style={{
                            fontSize: "0.83rem",
                            color: isDone ? "var(--text-light)" : "var(--text-dark)",
                            textDecoration: isDone ? "line-through" : "none",
                            transition: "color 0.2s",
                          }}>
                            {item}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <p style={{ fontSize: "0.72rem", color: "var(--text-light)", marginTop: "1rem", marginBottom: 0 }}>
          Based on NHS / RCPCH developmental guidance. Not a medical diagnosis tool —
          discuss any concerns with your health visitor or GP.
        </p>
      </div>
    </div>
  );
};

export default MilestoneChecklist;
